import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { Upload as UploadIcon, FileUpload as FileUploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { Location } from '../../types/Location';
import { DEFAULT_LANGUAGE_CODES, emptyLocalizedText, LocalizedText } from '../../types/LocalizedText';
import { GeocodingService } from '../../utils/geocodingService';
import { useLanguages } from '../../hooks/useLanguages';
import {
  formatLocalizedTextDisplay,
  hasLocalizedText,
  normalizeLocalizedText,
  serializeLocalizedText,
} from '../../utils/localizedText';

interface ExcelRow {
  [key: string]: unknown;
}

type ImportFieldKey = keyof Location | 'description' | `description_${string}`;

interface ImportField {
  key: ImportFieldKey;
  label: string;
  required?: boolean;
}

interface ColumnMapping {
  [excelColumn: string]: ImportFieldKey | null;
}

function parseBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1';
  }
  return Boolean(value);
}

function buildLocationFromRow(
  row: ExcelRow,
  columnMapping: ColumnMapping,
  languageCodes: string[],
  activeLanguages: ReturnType<typeof useLanguages>['activeLanguages']
): Partial<Location> {
  const location: Partial<Location> = {};
  const descriptionParts: LocalizedText = emptyLocalizedText(languageCodes);

  Object.entries(columnMapping).forEach(([excelCol, field]) => {
    if (!field || !(excelCol in row)) {
      return;
    }

    const value = row[excelCol];
    if (value === null || value === undefined || value === '') {
      return;
    }

    if (field === 'description') {
      location.description = normalizeLocalizedText(String(value), languageCodes);
      return;
    }

    if (typeof field === 'string' && field.startsWith('description_')) {
      descriptionParts[field.replace('description_', '')] = String(value);
      return;
    }

    if (field === 'isAccessible' || field === 'offersForWomen') {
      location[field] = parseBoolean(value);
      return;
    }

    (location as Record<string, unknown>)[field] = value;
  });

  const mergedDescription = {
    ...normalizeLocalizedText(location.description, languageCodes),
    ...descriptionParts,
  };

  if (hasLocalizedText(mergedDescription)) {
    location.description = serializeLocalizedText(mergedDescription, activeLanguages, {
      omitEmptyOptional: true,
    });
  }

  return location;
}

function hasRequiredDescription(
  description: LocalizedText | string | undefined,
  requiredLanguageCodes: string[]
): boolean {
  const normalized = normalizeLocalizedText(description);
  return requiredLanguageCodes.every((code) => Boolean(normalized[code]?.trim()));
}

interface LocationImportProps {
  open: boolean;
  onClose: () => void;
}

const LocationImport: React.FC<LocationImportProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { activeLanguages, languageCodes, requiredLanguages } = useLanguages();
  const effectiveLanguageCodes = languageCodes.length > 0 ? languageCodes : [...DEFAULT_LANGUAGE_CODES];
  const requiredLanguageCodes = useMemo(
    () =>
      requiredLanguages.length > 0
        ? requiredLanguages.map((language) => language.code)
        : [...DEFAULT_LANGUAGE_CODES],
    [requiredLanguages]
  );
  const [excelData, setExcelData] = useState<ExcelRow[]>([]);
  const [excelColumns, setExcelColumns] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [previewData, setPreviewData] = useState<Partial<Location>[]>([]);
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview'>('upload');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isIncremental, setIsIncremental] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validRowsCount, setValidRowsCount] = useState(0);

  const queryClient = useQueryClient();

  const locationFields = useMemo<ImportField[]>(() => {
    const baseFields: ImportField[] = [
      { key: 'id', label: t('locationImport.fieldLabels.id') },
      { key: 'name', label: t('locationImport.fieldLabels.name'), required: true },
      { key: 'street', label: t('locationImport.fieldLabels.street'), required: true },
      { key: 'zip', label: t('locationImport.fieldLabels.zip'), required: true },
      { key: 'city', label: t('locationImport.fieldLabels.city'), required: true },
      { key: 'country', label: t('locationImport.fieldLabels.country') },
      { key: 'phone', label: t('locationImport.fieldLabels.phone') },
      { key: 'email', label: t('locationImport.fieldLabels.email') },
      { key: 'website', label: t('locationImport.fieldLabels.website') },
      { key: 'description', label: t('locationImport.fieldLabels.descriptionAllLanguages') },
      { key: 'isAccessible', label: t('locationImport.fieldLabels.isAccessible') },
      { key: 'offersForWomen', label: t('locationImport.fieldLabels.offersForWomen') },
    ];

    const descriptionFields: ImportField[] = activeLanguages.map((language) => ({
      key: `description_${language.code}`,
      label: t('locationImport.fieldLabels.descriptionLanguage', { language: language.label }),
      required: language.isRequired !== false,
    }));

    const descriptionIndex = baseFields.findIndex((field) => field.key === 'description');
    return [
      ...baseFields.slice(0, descriptionIndex + 1),
      ...descriptionFields,
      ...baseFields.slice(descriptionIndex + 1),
    ];
  }, [activeLanguages, t]);

  const hasRequiredDescriptionMapping = useCallback(
    (mapping: ColumnMapping) => {
      const mappedFields = Object.values(mapping).filter((field) => field !== null);

      if (mappedFields.includes('description')) {
        return true;
      }

      return requiredLanguageCodes.every((code) =>
        mappedFields.includes(`description_${code}`)
      );
    },
    [requiredLanguageCodes]
  );

  const processExcelFile = useCallback((file: File) => {
    setError('');
    setIsProcessing(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON, skipping the first row (header)
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length < 2) {
          setError(t('locationImport.excelFileError'));
          setIsProcessing(false);
          return;
        }

        const headers = jsonData[0] as string[];
        const rows = jsonData.slice(1) as any[][];

        // Convert rows to objects
        const processedData: ExcelRow[] = rows.map(row => {
          const obj: ExcelRow = {};
          headers.forEach((header, index) => {
            obj[header] = row[index];
          });
          return obj;
        });

        setExcelColumns(headers);
        setExcelData(processedData);
        setStep('mapping');
        setIsProcessing(false);
      } catch (err) {
        setError(t('locationImport.errorReadingExcel'));
        setIsProcessing(false);
      }
    };

    reader.readAsArrayBuffer(file);
  }, [t]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processExcelFile(file);
  }, [processExcelFile]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files.length === 0) return;
    
    const file = files[0];
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'application/octet-stream' // fallback for some systems
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      setError(t('locationImport.invalidExcelFile'));
      return;
    }
    
    processExcelFile(file);
  }, [processExcelFile, t]);

  // Prevent default drag behaviors on the document
  React.useEffect(() => {
    const preventDefault = (e: DragEvent) => {
      e.preventDefault();
    };

    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  const handleColumnMapping = (excelColumn: string, locationField: ImportFieldKey | null) => {
    setColumnMapping(prev => ({
      ...prev,
      [excelColumn]: locationField
    }));
  };

  const isValidImportRow = useCallback(
    (location: Partial<Location>) =>
      Boolean(location.name && location.street && location.zip && location.city) &&
      hasRequiredDescription(location.description, requiredLanguageCodes),
    [requiredLanguageCodes]
  );

  const generatePreview = () => {
    const requiredFields = locationFields.filter(
      (field) => field.required && !String(field.key).startsWith('description_')
    );
    const mappedFields = Object.values(columnMapping).filter((field) => field !== null);
    const missingRequiredFields = requiredFields
      .filter((field) => !mappedFields.includes(field.key))
      .map((field) => field.label);

    if (missingRequiredFields.length > 0) {
      setError(t('locationImport.missingRequiredFields', { fields: missingRequiredFields.join(', ') }));
      return;
    }

    if (!hasRequiredDescriptionMapping(columnMapping)) {
      setError(t('locationImport.missingRequiredDescriptionMapping'));
      return;
    }

    const allValidRows = excelData
      .map((row) =>
        buildLocationFromRow(row, columnMapping, effectiveLanguageCodes, activeLanguages)
      )
      .filter(isValidImportRow);

    setValidRowsCount(allValidRows.length);

    const preview: Partial<Location>[] = allValidRows.slice(0, 5);

    setPreviewData(preview);
    setStep('preview');
    setError(''); // Clear any previous errors
  };

  const importMutation = useMutation({
    mutationFn: async (locations: Partial<Location>[]) => {
      return axios.post(`${process.env.REACT_APP_API_URL}/locations/batch`, {
        locations,
        isIncremental
      });
    },
    onSuccess: (response) => {
      const { summary } = response.data;
      console.log('Batch import completed:', summary);
      const successMessage = t('locationImport.importSuccess', { count: summary.created + summary.updated });
      setSuccess(successMessage);
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setTimeout(() => {
        onClose();
        setExcelData([]);
        setExcelColumns([]);
        setColumnMapping({});
        setPreviewData([]);
        setStep('upload');
        setError('');
        setSuccess('');
      }, 3000); // Show success message for 3 seconds before closing
    },
    onError: (error: any) => {
      setError(t('locationImport.importError'));
    }
  });

  const handleImport = async () => {
    setIsProcessing(true);
    setError('');
    const locationsToImport: Partial<Location>[] = [];
    let geocodeError = '';

    for (const row of excelData) {
      // Skip rows that are completely empty (all values are undefined/null/empty)
      const hasAnyData = Object.values(row).some(value => 
        value !== null && value !== undefined && value !== ''
      );
      
      if (!hasAnyData) {
        console.log('Skipping empty row:', row);
        continue;
      }

      const location = buildLocationFromRow(
        row,
        columnMapping,
        effectiveLanguageCodes,
        activeLanguages
      );

      if (!isValidImportRow(location)) {
        continue;
      }
      
      // Geocode address
      try {
        const coords = await GeocodingService.geocodeAddress({
          street: String(location.street || ''),
          zip: String(location.zip || ''),
          city: String(location.city || ''),
          country: String(location.country || 'Deutschland'),
        });
        if (!coords) {
          geocodeError = `Geocoding failed for address: ${location.street}, ${location.zip} ${location.city}, ${location.country}`;
          break;
        }
        location.coordinates = coords;
      } catch (err) {
        geocodeError = `Geocoding error for address: ${location.street}, ${location.zip} ${location.city}, ${location.country}`;
        break;
      }
      locationsToImport.push(location);
    }

    setIsProcessing(false);
    if (geocodeError) {
      setError(geocodeError);
      return;
    }

    importMutation.mutate(locationsToImport);
  };

  const resetImport = () => {
    setExcelData([]);
    setExcelColumns([]);
    setColumnMapping({});
    setPreviewData([]);
    setStep('upload');
    setError('');
    setSuccess('');
    setIsIncremental(false);
    setValidRowsCount(0);
  };

  const downloadTemplate = () => {
    const descriptionSamples: Record<string, string> = {
      de: 'Dies ist eine Beispiel-Beschreibung',
      en: 'This is a sample location description',
    };

    const buildDescriptionColumns = (suffix: string) =>
      Object.fromEntries(
        effectiveLanguageCodes.map((code) => [
          `description_${code}`,
          descriptionSamples[code] ?? `Sample description (${code}) ${suffix}`,
        ])
      );

    const templateData = [
      {
        id: '550e8400-e29b-41d4-a716-446655440000',
        name: 'Sample Location',
        street: '123 Main Street',
        zip: '12345',
        city: 'Sample City',
        country: 'Deutschland',
        phone: '+49 123 456789',
        email: 'info@samplelocation.com',
        website: 'https://samplelocation.com',
        ...buildDescriptionColumns('A'),
        isAccessible: 'true',
        offersForWomen: 'true',
      },
      {
        id: '6ba7b810-9dad-11d1-80b4-00c04fd430c8',
        name: 'Another Location',
        street: '456 Oak Avenue',
        zip: '67890',
        city: 'Another City',
        country: 'Deutschland',
        phone: '+49 987 654321',
        email: 'contact@anotherlocation.com',
        website: 'https://anotherlocation.com',
        ...buildDescriptionColumns('B'),
        isAccessible: 'false',
        offersForWomen: 'false',
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Locations Template');
    
    // Generate and download the file
    XLSX.writeFile(workbook, 'locations_import_template.xlsx');
  };

  const renderUploadStep = () => (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
        {t('locationImport.uploadExcelFile')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('locationImport.uploadDescription')}
      </Typography>
      
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Paper 
          elevation={0}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{ 
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.dark' : 'primary.main',
            borderRadius: 3,
            p: 4,
            mb: 4,
            bgcolor: isDragOver ? 'primary.100' : 'primary.50',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: isDragOver ? 'primary.100' : 'primary.100',
              borderColor: 'primary.dark',
            }
          }}
        >
          <input
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            id="excel-file-upload"
            type="file"
            onChange={handleFileUpload}
            disabled={isProcessing}
          />
          <label htmlFor="excel-file-upload" style={{ cursor: 'pointer', display: 'block' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {isProcessing ? (
                <CircularProgress size={60} />
              ) : (
                <FileUploadIcon sx={{ fontSize: 60, color: 'primary.main' }} />
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {isProcessing ? t('locationImport.processingFile') : isDragOver ? t('locationImport.dropExcelFileHere') : t('locationImport.chooseExcelFile')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isProcessing 
                  ? t('locationImport.pleaseWaitProcessing')
                  : isDragOver 
                    ? t('locationImport.releaseToUpload')
                    : t('locationImport.clickToBrowseOrDrag')
                }
              </Typography>
            </Box>
          </label>
        </Paper>
        
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadTemplate}
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1.5,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
            }
          }}
        >
          {t('locationImport.downloadTemplate')}
        </Button>
      </Box>
    </Box>
  );

  const renderMappingStep = () => (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
        {t('locationImport.mapExcelColumns')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('locationImport.mapDescription')}
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {excelColumns.map((excelColumn) => (
            <Box 
              key={excelColumn} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: columnMapping[excelColumn] ? 'success.50' : 'grey.50',
                border: '1px solid',
                borderColor: columnMapping[excelColumn] ? 'success.200' : 'grey.200',
              }}
            >
              <Typography sx={{ minWidth: 150, mr: 3, fontWeight: 500 }}>
                {excelColumn}
              </Typography>
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel>{t('locationImport.mapToField')}</InputLabel>
                <Select
                  value={columnMapping[excelColumn] || ''}
                  onChange={(e) => handleColumnMapping(excelColumn, (e.target.value as ImportFieldKey) || null)}
                  label={t('locationImport.mapToField')}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>{t('locationImport.skipThisColumn')}</em>
                  </MenuItem>
                  {locationFields.map((field) => (
                    <MenuItem key={field.key} value={field.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{field.label}</span>
                        {field.required && (
                          <Chip 
                            label={t('locationImport.required')} 
                            size="small" 
                            color="error" 
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {columnMapping[excelColumn] && (
                <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={t('locationImport.mapped')} 
                    size="small" 
                    color="success" 
                    icon={<span>✓</span>}
                    sx={{ height: 24 }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
        <Typography variant="body2">
          <strong>{t('locationImport.tips')}</strong>
          <br />{t('locationImport.tipsIdOptional')}
          <br />{t('locationImport.tipsBooleanFields')}
          <br />{t('locationImport.tipsRequiredFields')}
          <br />{t('locationImport.tipsLocalizedDescription')}
        </Typography>
      </Alert>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={generatePreview}
          disabled={!Object.values(columnMapping).some(field => field !== null)}
        >
          {t('locationImport.generatePreview')}
        </Button>
      </Box>
    </Box>
  );

  const renderPreviewStep = () => (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
        {t('locationImport.previewImportData')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('locationImport.previewDescription', { validCount: validRowsCount, totalCount: excelData.length })}
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {t('locationImport.importType')}
        </Typography>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 2, 
                border: 2, 
                borderColor: !isIncremental ? 'primary.main' : 'grey.300', 
                borderRadius: 2, 
                bgcolor: !isIncremental ? 'primary.50' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: !isIncremental ? 'primary.dark' : 'grey.400',
                  bgcolor: !isIncremental ? 'primary.100' : 'grey.50',
                }
              }}
              onClick={() => setIsIncremental(false)}
            >
              <input
                type="radio"
                id="absolute-import"
                name="import-type"
                checked={!isIncremental}
                onChange={() => setIsIncremental(false)}
                style={{ marginRight: 16 }}
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('locationImport.fullImport')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('locationImport.fullImportDescription')}
                </Typography>
              </Box>
            </Box>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 2, 
                border: 2, 
                borderColor: isIncremental ? 'primary.main' : 'grey.300', 
                borderRadius: 2, 
                bgcolor: isIncremental ? 'primary.50' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: isIncremental ? 'primary.dark' : 'grey.400',
                  bgcolor: isIncremental ? 'primary.100' : 'grey.50',
                }
              }}
              onClick={() => setIsIncremental(true)}
            >
              <input
                type="radio"
                id="incremental-import"
                name="import-type"
                checked={isIncremental}
                onChange={() => setIsIncremental(true)}
                style={{ marginRight: 16 }}
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('locationImport.incrementalImport')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('locationImport.incrementalImportDescription')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </FormControl>
      </Paper>

      {!isIncremental && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> Absolute import will delete all existing locations not included in this import file. 
            Make sure you have a backup of your current data before proceeding.
          </Typography>
        </Alert>
      )}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                {locationFields.map((field) => (
                  <TableCell key={field.key} sx={{ fontWeight: 600, borderBottom: '2px solid', borderColor: 'grey.300' }}>
                    {field.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewData.map((location, index) => (
                <TableRow key={index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                  {locationFields.map((field) => (
                    <TableCell key={field.key} sx={{ borderBottom: '1px solid', borderColor: 'grey.200' }}>
                      {field.key === 'isAccessible' || field.key === 'offersForWomen' ? (
                        <Chip 
                          label={location[field.key] ? 'Yes' : 'No'} 
                          size="small" 
                          color={location[field.key] ? 'success' : 'default'}
                          sx={{ borderRadius: 1 }}
                        />
                      ) : field.key === 'id' ? (
                        location[field.key] ? (
                          <Chip 
                            label={String(location[field.key]).substring(0, 8) + '...'} 
                            size="small" 
                            variant="outlined"
                            sx={{ borderRadius: 1 }}
                          />
                        ) : (
                          <Chip 
                            label="Auto-generated" 
                            size="small" 
                            color="info"
                            sx={{ borderRadius: 1 }}
                          />
                        )
                      ) : field.key === 'coordinates' ? (
                        <Chip 
                          label={location[field.key] ? 'Set' : 'Not set'} 
                          size="small" 
                          color={location[field.key] ? 'success' : 'warning'}
                          sx={{ borderRadius: 1 }}
                        />
                      ) : field.key === 'categories' ? (
                        <Chip 
                          label={location.categories && Object.keys(location.categories).length > 0 ? 'Configured' : 'Not configured'} 
                          size="small" 
                          color={location.categories && Object.keys(location.categories).length > 0 ? 'success' : 'default'}
                          sx={{ borderRadius: 1 }}
                        />
                      ) : field.key === 'description' ? (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {(() => {
                            const text = formatLocalizedTextDisplay(location.description, effectiveLanguageCodes);
                            return text.length > 40 ? `${text.substring(0, 40)}...` : text;
                          })()}
                        </Typography>
                      ) : typeof field.key === 'string' && field.key.startsWith('description_') ? (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {(() => {
                            const code = field.key.replace('description_', '');
                            const text = normalizeLocalizedText(location.description, effectiveLanguageCodes)[code] || '-';
                            return text.length > 40 ? `${text.substring(0, 40)}...` : text;
                          })()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {String((location as Record<string, unknown>)[field.key] ?? '-')}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <UploadIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" component="div">
          {t('locations.importLocations')}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            {success}
          </Alert>
        )}

        {(importMutation.isPending || isProcessing) && (
          <Box sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'rgba(0, 0, 0, 0.5)', 
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Paper sx={{ 
              p: 4, 
              borderRadius: 3, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 2,
              minWidth: 300
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                {importMutation.isPending ? t('locationImport.importing') : t('locationImport.processingFile')}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {importMutation.isPending 
                  ? t('locationImport.importing')
                  : t('locationImport.processingFile')
                }
                <br />
                This may take a few moments.
              </Typography>
            </Paper>
          </Box>
        )}

        {step === 'upload' && renderUploadStep()}
        {step === 'mapping' && renderMappingStep()}
        {step === 'preview' && renderPreviewStep()}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        pt: 2, 
        mt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: 1
      }}>
        {step === 'mapping' && (
          <Button 
            onClick={resetImport}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('common.back')}
          </Button>
        )}
        {step === 'preview' && (
          <Button 
            onClick={() => setStep('mapping')}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('common.back')}
          </Button>
        )}
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          {t('common.cancel')}
        </Button>
        {step === 'preview' && (
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={importMutation.isPending || isProcessing}
            startIcon={importMutation.isPending || isProcessing ? <CircularProgress size={20} /> : <UploadIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
              }
            }}
          >
            {importMutation.isPending || isProcessing ? t('locationImport.importing') : t('locationImport.import')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LocationImport; 